/**
 * URL検証ユーティリティ
 *
 * MDSファイルのURL読み込み時のセキュリティチェックを提供します。
 * 三段階の検証を実施：
 * 1. 完全ブロック: クラウドメタデータエンドポイント
 * 2. ホワイトリスト: 信頼された特定のURL（URL単位で管理）
 *    - ハードコードされた公式URL
 *    - ユーザーが設定で追加したURL
 * 3. 警告: その他のURL（プライベートIP含む）
 *
 * 注: 不特定多数がファイルをアップロードできるサービス（GitHub、S3、OneDriveなど）は
 * セキュリティ上の理由からホワイトリストに含めず、警告を表示します。
 */

import { useSettingsStore } from '../stores/settingsStore';

export interface UrlValidationResult {
  /** 読み込みを許可するか */
  allowed: boolean;
  /** 警告を表示するか */
  needsWarning: boolean;
  /** ブロックまたは警告の理由 */
  reason?: string;
}

/**
 * クラウドメタデータエンドポイント
 * AWS/GCP/Azureのメタデータ APIはIAMクレデンシャルを返すため完全ブロック
 */
const METADATA_ENDPOINTS = [
  '169.254.169.254', // AWS, GCP, Azure
  'metadata.google.internal', // GCP
  '100.100.100.200', // Alibaba Cloud
  'fd00:ec2::254', // AWS IPv6
];

/**
 * ビルトインホワイトリスト: 公式の信頼されたURL
 * これらはコードに埋め込まれ、ユーザーは削除できません。
 *
 * マッチングルール：
 * - 末尾がスラッシュ（/）：プレフィックスマッチング（ディレクトリ配下すべて許可）
 *   例: 'https://example.com/data/' → https://example.com/data/file.mds はOK
 * - 末尾がスラッシュなし：完全一致（特定のURLのみ許可）
 *   例: 'https://example.com/file.mds' → https://example.com/file.mds のみOK
 */
const BUILTIN_TRUSTED_URLS: string[] = [
  'https://raw.githubusercontent.com/izuru-matsuura/', // MIDAS公式データリポジトリ
];

/**
 * プライベートIPアドレスの範囲
 */
const PRIVATE_IP_RANGES = [
  /^10\./,
  /^172\.(1[6-9]|2\d|3[01])\./,
  /^192\.168\./,
  /^127\./,
  /^localhost$/i,
];

/**
 * URLパターンとマッチするか確認
 *
 * マッチングルール：
 * - 末尾がスラッシュ（/）：プレフィックスマッチング
 * - 末尾がスラッシュなし：完全一致
 */
function matchesTrustedPattern(url: string, pattern: string): boolean {
  if (pattern.endsWith('/')) {
    // 末尾がスラッシュ：プレフィックスマッチング（ディレクトリ配下すべて許可）
    return url.startsWith(pattern);
  } else {
    // 末尾がスラッシュなし：完全一致（特定のURLのみ許可）
    return url === pattern;
  }
}

/**
 * URLがホワイトリストに含まれているか確認
 * ビルトインURLとユーザー設定のURLの両方をチェック
 */
function isTrustedUrl(url: string): boolean {
  // ビルトインURLをチェック
  if (BUILTIN_TRUSTED_URLS.some((pattern) => matchesTrustedPattern(url, pattern))) {
    return true;
  }

  // ユーザー設定のURLをチェック
  const userTrustedUrls = useSettingsStore.getState().getTrustedUrls();
  return userTrustedUrls.some((pattern) => matchesTrustedPattern(url, pattern));
}

/**
 * プライベートIPアドレスか確認
 */
function isPrivateIp(hostname: string): boolean {
  return PRIVATE_IP_RANGES.some((pattern) => pattern.test(hostname));
}

/**
 * クラウドメタデータエンドポイントか確認
 */
function isMetadataEndpoint(hostname: string): boolean {
  return METADATA_ENDPOINTS.includes(hostname);
}

/**
 * MDSファイルのURLを検証
 *
 * @param urlString - 検証するURL文字列
 * @returns 検証結果
 */
export function validateMdsUrl(urlString: string): UrlValidationResult {
  let url: URL;

  try {
    url = new URL(urlString);
  } catch {
    return {
      allowed: false,
      needsWarning: false,
      reason: 'Invalid URL format',
    };
  }

  // プロトコルチェック: HTTP/HTTPSのみ許可
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return {
      allowed: false,
      needsWarning: false,
      reason: `Unsupported protocol: ${url.protocol}. Only HTTP and HTTPS are allowed.`,
    };
  }

  const hostname = url.hostname.toLowerCase();

  // レベル1: クラウドメタデータエンドポイントを完全ブロック
  if (isMetadataEndpoint(hostname)) {
    return {
      allowed: false,
      needsWarning: false,
      reason: 'Access to cloud metadata endpoints is blocked for security reasons.',
    };
  }

  // レベル2: ホワイトリストURL（警告なし）
  if (isTrustedUrl(urlString)) {
    return {
      allowed: true,
      needsWarning: false,
    };
  }

  // レベル3: その他すべて（プライベートIP含む）は警告
  let reason = 'This URL is not in the trusted URL list.';

  if (isPrivateIp(hostname)) {
    reason = 'This URL points to a private IP address or localhost.';
  }

  return {
    allowed: true,
    needsWarning: true,
    reason,
  };
}
